import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import type { ClientsTranslations } from "@/shared/lang/interfaces/clients";
import useTranslations from "@/shared/lang/useTranslations";

import { PAGINATION_SIZES } from "../constants/clients.constants";

export interface TablePaginationFooterProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Translation key of the "Showing X–Y of Z …" summary, so each table names its entity. */
  summaryKey: keyof Pick<ClientsTranslations, "orgs_pagination_summary" | "clients_pagination_summary">;
}

const PAGE_SIZE_OPTIONS = PAGINATION_SIZES.map((size) => ({ value: String(size), label: String(size) }));

/** Shared table footer: page-size selector + localized summary on the left, page navigation on the right. */
export function TablePaginationFooter({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  summaryKey,
}: TablePaginationFooterProps) {
  const { t } = useTranslations("clients");
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <ColocaPagination
      page={page}
      perPage={pageSize}
      total={total}
      onPageChange={onPageChange}
      withSeparator={false}
      size="sm"
      summary={
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs">{t("pagination_show_label")}</span>
          <ColocaSelect
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
            options={PAGE_SIZE_OPTIONS}
            className="h-8 w-18"
            aria-label={t("pagination_show_label")}
          />
          <span className="text-xs">
            {t(summaryKey, {
              from: from.toLocaleString(),
              to: to.toLocaleString(),
              total: total.toLocaleString(),
            })}
          </span>
        </div>
      }
    />
  );
}
